import type { AdminKpisFilterPanelProps } from "../../models/admin-kpis.models";
import DateFilter from "./DateFilter";
import AdminKpisMultiSelectFilter from "./AdminKpisMultiSelectFilter";
import { ArrowPathIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { memo } from "react";
import { twMerge } from "tailwind-merge";

const AdminKpisFilterPanel = ({
  filters,
  filterOptions,
  isRefreshing,
  onChange,
  onRefresh,
  onReset,
}: AdminKpisFilterPanelProps) => {
  return (
    <aside
      data-test="admin-kpis-filter-panel"
      className="h-full w-72 flex-none overflow-y-auto border-r border-[#D7E2DE] bg-white p-4 dark:border-gray-700 dark:bg-gray-900"
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">Filter Options</h2>
        <div className="flex flex-none items-center gap-1">
          <button
            type="button"
            title="Refresh KPIs"
            aria-label="Refresh KPIs"
            data-test="admin-kpis-refresh-button"
            disabled={isRefreshing}
            className="flex h-8 w-8 items-center justify-center border border-transparent bg-transparent text-green transition-colors hover:border-[#D7E2DE] disabled:cursor-not-allowed disabled:opacity-60 dark:hover:border-gray-700"
            onClick={onRefresh}
          >
            <ArrowPathIcon className={twMerge("h-4 w-4", isRefreshing && "animate-spin")} />
          </button>
          <button
            type="button"
            title="Reset filters"
            aria-label="Reset filters"
            data-test="admin-kpis-reset-filters-button"
            className="flex h-8 w-8 items-center justify-center border border-transparent bg-transparent text-[#4A5655] transition-colors hover:border-[#D7E2DE] hover:text-green dark:text-gray-200 dark:hover:border-gray-700"
            onClick={onReset}
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="grid gap-3">
        <div>
          <div className="mb-1 text-[11px] font-medium text-[#6B7473] dark:text-gray-300">In fleet</div>
          <div className="grid grid-cols-2 border border-[#D7E2DE] bg-white dark:border-gray-700 dark:bg-gray-900">
            {filterOptions.inFleet.map((option) => (
              <button
                key={String(option)}
                type="button"
                data-test={`admin-kpis-in-fleet-${option}`}
                className={twMerge(
                  "h-8 text-xs font-medium transition-colors",
                  filters.inFleet === option
                    ? "bg-green text-white"
                    : "text-[#4A5655] hover:bg-[#E9F4F3] dark:text-gray-200 dark:hover:bg-gray-800",
                )}
                onClick={() => onChange({ ...filters, inFleet: option })}
              >
                {String(option)}
              </button>
            ))}
          </div>
        </div>

        <AdminKpisMultiSelectFilter
          label="Contract Type"
          options={filterOptions.contractTypes}
          value={filters.contractTypes}
          dataTest="admin-kpis-filter-contract-type"
          onChange={(contractTypes) => onChange({ ...filters, contractTypes })}
        />
        <AdminKpisMultiSelectFilter
          label="Vessel Names"
          options={filterOptions.vesselNames}
          value={filters.vesselNames}
          dataTest="admin-kpis-filter-vessel-names"
          onChange={(vesselNames) => onChange({ ...filters, vesselNames })}
        />
        <AdminKpisMultiSelectFilter
          label="Vessel Types"
          options={filterOptions.vesselTypes}
          value={filters.vesselTypes}
          dataTest="admin-kpis-filter-vessel-types"
          onChange={(vesselTypes) => onChange({ ...filters, vesselTypes })}
        />
        <AdminKpisMultiSelectFilter
          label="Document Type"
          options={filterOptions.documentTypes}
          value={filters.documentTypes}
          dataTest="admin-kpis-filter-document-type"
          onChange={(documentTypes) => onChange({ ...filters, documentTypes })}
        />
        <AdminKpisMultiSelectFilter
          label="Document Name"
          options={filterOptions.documentNames}
          value={filters.documentNames}
          dataTest="admin-kpis-filter-document-name"
          onChange={(documentNames) => onChange({ ...filters, documentNames })}
        />
        <DateFilter
          label="Expiration date before"
          value={filters.expirationDateBefore}
          dataTest="admin-kpis-filter-expiration-date-before"
          onChange={(expirationDateBefore) => onChange({ ...filters, expirationDateBefore })}
        />
        <DateFilter
          label="Survey date before"
          value={filters.surveyDateBefore}
          dataTest="admin-kpis-filter-survey-date-before"
          onChange={(surveyDateBefore) => onChange({ ...filters, surveyDateBefore })}
        />
        <DateFilter
          label="Open date before"
          value={filters.openDateBefore}
          dataTest="admin-kpis-filter-open-date-before"
          onChange={(openDateBefore) => onChange({ ...filters, openDateBefore })}
        />
        <AdminKpisMultiSelectFilter
          label="Alerts"
          options={filterOptions.alerts}
          value={filters.alerts}
          dataTest="admin-kpis-filter-alerts"
          onChange={(alerts) => onChange({ ...filters, alerts })}
        />
      </div>
    </aside>
  );
};

export default memo(AdminKpisFilterPanel);
